/**
 * i18n Initialization (v4.8.457)
 *
 * Unified, mission-critical initialization for Aureus client-side localization.
 *
 * Architecture:
 *   - i18next + react-i18next, JSON catalogs bundled (no HTTP backend).
 *   - Single source of truth for language state is `lib/languageResolver.ts`.
 *   - Synchronous boot is English, a language chosen on this browser, or the
 *     signed-in account's saved pref_language.
 *   - Async hydration applies users.pref_language when a session exists.
 *     Browser language and country are not used.
 *
 * Boundaries:
 *   - These catalogs are Layer A (static, financial/legal/UI-safe).
 *   - Layer B (runtime machine translation for marketing/info copy only) lives in
 *     `lib/services/translationService.ts` and is NEVER invoked by i18next.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  rememberAccountLanguage,
  resolveBootLanguage,
  resolveInitialLanguage,
  setStoredLanguage,
  isSupported,
  SUPPORTED_LANGUAGES,
} from './languageResolver';
import { getSessionHeaders } from './serverSession';

// Static catalog imports. Empty catalogs fall back to 'en' automatically via i18next.
import enCommon from '../locales/en/common.json';
import frCommon from '../locales/fr/common.json';
import ptCommon from '../locales/pt/common.json';
import esCommon from '../locales/es/common.json';
import arCommon from '../locales/ar/common.json';
import swCommon from '../locales/sw/common.json';
import zuCommon from '../locales/zu/common.json';
import hiCommon from '../locales/hi/common.json';
import zhCommon from '../locales/zh/common.json';

const resources = {
  en: { common: enCommon },
  fr: { common: frCommon },
  pt: { common: ptCommon },
  es: { common: esCommon },
  ar: { common: arCommon },
  sw: { common: swCommon },
  zu: { common: zuCommon },
  hi: { common: hiCommon },
  zh: { common: zhCommon },
} as const;

/**
 * Synchronous boot language.
 * English, unless this browser has an explicit choice or the signed-in
 * account already has a saved language. Country and browser language are ignored.
 */
function bootLanguage(): string {
  try { return resolveBootLanguage(); } catch (_) { return 'en'; }
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: bootLanguage(),
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),
    nonExplicitSupportedLngs: false,
    ns: ['common'],
    defaultNS: 'common',
    // Flat dotted keys: 'auth.login.signInButton' is one literal key, not a nested path.
    // This matches the established catalog convention (see locales/en/common.json).
    keySeparator: false,
    interpolation: { escapeValue: false },
    initImmediate: true,
    debug: false,
    returnNull: false,
    returnEmptyString: false,
  })
  .catch((err) => {
    console.error('i18n initialization failed:', err);
  });

/**
 * Apply RTL direction for Arabic. Called whenever language changes.
 * Safe no-op on SSR.
 */
function applyDocumentDirection(code: string): void {
  if (typeof document === 'undefined') return;
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  const dir = lang?.rtl ? 'rtl' : 'ltr';
  try {
    document.documentElement.setAttribute('lang', code);
    document.documentElement.setAttribute('dir', dir);
  } catch (_) { /* ignore */ }
}

// Keep <html lang> and direction in sync with i18n state.
i18n.on('languageChanged', applyDocumentDirection);
applyDocumentDirection(i18n.language || 'en');

/**
 * Async hydration. Pass `users.pref_language` when it is known.
 * A saved account language is applied. Otherwise English, unless the
 * visitor already chose a language on this browser.
 */
export async function hydrateLanguage(dbValue?: string | null): Promise<string> {
  try {
    const { code } = await resolveInitialLanguage(dbValue ?? undefined);
    if (isSupported(code) && code !== i18n.language) {
      await i18n.changeLanguage(code);
    }
    return code;
  } catch (err) {
    console.warn('hydrateLanguage failed:', err);
    return i18n.language || 'en';
  }
}

/**
 * Read users.pref_language for the signed-in account and apply it.
 * No session, or an empty preference, leaves English or the visitor's own choice.
 */
export async function applyAccountPreferredLanguage(): Promise<string> {
  if (typeof window === 'undefined') return i18n.language || 'en';
  let headers: Record<string, string> = {};
  try { headers = getSessionHeaders(); } catch (_) { return i18n.language || 'en'; }
  if (!headers['X-Session-Id']) return i18n.language || 'en';
  try {
    const response = await fetch('/api/user/pref-language', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ operation: 'current' }),
    });
    if (!response.ok) return i18n.language || 'en';
    const data = await response.json().catch(() => null) as { impersonated?: boolean; prefLanguage?: string | null } | null;
    if (!data || data.impersonated) return i18n.language || 'en';
    if (isSupported(data.prefLanguage)) return hydrateLanguage(data.prefLanguage);
    rememberAccountLanguage(null);
    const { code } = await resolveInitialLanguage(null);
    if (isSupported(code) && code !== i18n.language) await i18n.changeLanguage(code);
    return code;
  } catch (err) {
    console.warn('applyAccountPreferredLanguage failed:', err);
    return i18n.language || 'en';
  }
}

/** Programmatic language change with persistence. */
export async function changeLanguage(code: string, opts?: { explicit?: boolean }): Promise<void> {
  if (!isSupported(code)) return;
  setStoredLanguage(code, { explicit: opts?.explicit === true });
  await i18n.changeLanguage(code);
}

export default i18n;